import cx from 'classnames';
import {Fragment, PureComponent, createRef} from 'react';
import {withTranslation, WithTranslation} from 'react-i18next';
import {Room, LocalVideoTrack, RemoteParticipant, RemoteTrack} from 'twilio-video';

import {ConfirmModal, Icon} from 'components';
import {SCREEN_SHARING_VIDEO_TRACK} from 'constants/session';
import {NOT_ALLOWED_ERROR} from 'constants/webrtc';
import {ScreenSharingActivityStatus} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {logger} from 'utils';
import './styles.scss';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.WEBRTC.SCREEN_SHARING;

type Props = {
    readonly room: Room;
    readonly isPresenter: boolean;
    readonly messageClassName?: string;
    readonly mini?: boolean;
    readonly confirmRequired?: boolean;
    readonly columnLayout?: boolean;
    readonly onStop?: (error?: any) => void;
    readonly onStart?: () => void;
    readonly status: ScreenSharingActivityStatus;
} & WithTranslation;

type State = {
    localVideoTrack: LocalVideoTrack;
    screenSharingTrack: MediaStreamTrack;
    sharingMessageEnabled: boolean;
    confirmPresenterDialogOpen: boolean;
};

const initialState: State = {
    localVideoTrack: null,
    screenSharingTrack: null,
    sharingMessageEnabled: true,
    confirmPresenterDialogOpen: false
};

class TwilioScreenSharing extends PureComponent<Props, State> {
    readonly state: State = initialState;

    remoteMediaRef = createRef<HTMLDivElement>();

    _isMounted = false;

    componentDidMount() {
        this._isMounted = true;

        if (this.props.isPresenter) {
            if (this.props.confirmRequired) {
                this.setState({confirmPresenterDialogOpen: true});
            } else {
                this.startScreenSharing();
            }
        } else {
            this.subscribeRoomEvents();
        }
    }

    componentWillUnmount() {
        if (this.props.isPresenter && this.props.status === ScreenSharingActivityStatus.Accepted) {
            this.stopScreenSharing();
        }

        this._isMounted = false;
    }

    closeConfirmPresenterDialogOpen = (confirm: boolean) => {
        this.setState({confirmPresenterDialogOpen: false});
        if (confirm) {
            this.startScreenSharing();
        } else {
            this.stopScreenSharing({message: this.props.t(T_KEYS.REJECTED)});
        }
    };

    subscribeRoomEvents = () => {
        const {room} = this.props;

        room.participants.forEach((p: RemoteParticipant) => {
            this.participantConnected(p);
        });

        room.on('participantConnected', (p: RemoteParticipant) => {
            this.participantConnected(p);
        });

        // Detach all participant’s track when they leave a room.
        room.on('participantDisconnected', (participant) => {
            this.detachParticipantTracks(participant);
        });

        room.on('disconnected', () => {
            this.detachParticipantTracks(room.localParticipant);
            room.participants.forEach((p) => this.detachParticipantTracks(p));
        });
    };

    participantConnected = (p: RemoteParticipant) => {
        const remoteMediaEl = this.remoteMediaRef.current as HTMLElement;
        p.on('trackSubscribed', (track: RemoteTrack) => {
            this.attachTracks([track], remoteMediaEl);
        });
        p.on('trackUnsubscribed', (track: RemoteTrack) => {
            this.detachTracks([track]);
        });
    };

    attachTracks = (tracks, container) => {
        tracks
            .filter((track) => track.name === SCREEN_SHARING_VIDEO_TRACK)
            .forEach((track) => {
                container.appendChild(track.attach());
                this.props.onStart?.();
            });
    };

    detachTracks = (tracks) => {
        tracks
            .filter((track) => track.name === SCREEN_SHARING_VIDEO_TRACK)
            .forEach((track) => {
                track.detach().forEach((detachedElement) => {
                    detachedElement.remove();
                    detachedElement.srcObject = null;
                });
            });
    };

    detachParticipantTracks = (participant) => {
        const tracks = Array.from(participant.tracks.values());
        this.detachTracks(tracks);
    };

    startScreenSharing = async () => {
        const localParticipant = this.props.room.localParticipant;
        const mediaDevices = navigator.mediaDevices as any;

        if (!mediaDevices || !mediaDevices.getDisplayMedia) {
            this.stopScreenSharing({
                message: T_KEYS.NOT_SUPPORTED
            });
            return;
        }

        try {
            const stream = await mediaDevices.getDisplayMedia();
            const screenSharingTrack = stream.getTracks()[0];
            screenSharingTrack.addEventListener('ended', this.handleScreenSharingTrackEnded.bind(this));

            const localVideoTrack = new LocalVideoTrack(screenSharingTrack, {
                name: SCREEN_SHARING_VIDEO_TRACK,
                logLevel: 'off'
            });

            localParticipant.publishTrack(localVideoTrack);
            this.setState({
                localVideoTrack,
                screenSharingTrack
            });
            this.props.onStart?.();
            this.enableSharingMessage();
        } catch (e: any) {
            const message = this.props.t(e.name === NOT_ALLOWED_ERROR ? T_KEYS.NOT_ALLOWED : T_KEYS.ERROR);

            logger.warn({category: logger.category.Twilio, message: `Cannot start screen sharing: ${e.message}`});
            this.stopScreenSharing({message});
        }
    };

    stopScreenSharing = (error?: any) => {
        const {localVideoTrack, screenSharingTrack} = this.state;
        const {room, onStop} = this.props;

        if (localVideoTrack) {
            room.localParticipant.unpublishTrack(localVideoTrack);
        }

        screenSharingTrack?.stop();

        onStop?.(error);

        if (this._isMounted) {
            this.setState({
                localVideoTrack: null,
                screenSharingTrack: null,
            });
        }
    };

    handleScreenSharingTrackEnded = () => {
        const {t} = this.props;
        this.stopScreenSharing({message: t(T_KEYS.REJECTED)});
    };

    enableSharingMessage = () => {
        this.setState({sharingMessageEnabled: true});
    };

    dismissSharingMessage = () => {
        this.setState({sharingMessageEnabled: false});
    };

    render() {
        const {t, isPresenter, status, messageClassName, confirmRequired, columnLayout, mini} = this.props;
        const {
            remoteMediaRef, dismissSharingMessage, closeConfirmPresenterDialogOpen,
            state: {sharingMessageEnabled, confirmPresenterDialogOpen}
        } = this;

        const containerStyle = columnLayout ? {maxWidth: '75%'} : {};

        return (
            <Fragment>
                {
                    isPresenter ?
                        (
                            status === ScreenSharingActivityStatus.Accepted && sharingMessageEnabled &&
                                <div className={cx(messageClassName,
                                    'tw-text-center tw-z-1 tw-px-5 tw-py-3 tw-bg-layout tw-shadow-md tw-rounded-md',
                                    'tw-flex tw-items-center')}
                                >
                                    <div className='tw-bg-default tw-rounded-full tw-flex-center tw-w-8 tw-h-8 tw-mr-4'>
                                        <Icon name='share-screen-out' size='sm' />
                                    </div>
                                    {t(T_KEYS.IN_PROGRESS)}
                                    <Icon
                                        name='close'
                                        button
                                        margin={false}
                                        className='tw-ml-4'
                                        color='dark'
                                        onClick={dismissSharingMessage}
                                    />
                                </div>
                        ) : (
                            !mini &&
                                <div
                                    ref={remoteMediaRef}
                                    className='screen-sharing-video-container tw-flex-center tw-absolute tw-inset-0'
                                    style={containerStyle}
                                />
                        )
                }
                {confirmRequired &&
                    <ConfirmModal
                        open={confirmPresenterDialogOpen}
                        title={t(T_KEYS.REQUESTED_MESSAGE)}
                        okText={t(TRANSLATION_KEYS.COMMON.BUTTONS.ALLOW)}
                        cancelText={t(TRANSLATION_KEYS.COMMON.BUTTONS.REJECT)}
                        onClose={closeConfirmPresenterDialogOpen}
                    />}
            </Fragment>
        );
    }
}

export default withTranslation()(TwilioScreenSharing);
